import { useMutation } from "@tanstack/react-query"; // Import useMutation
import { saveAs } from "file-saver";
import JSZip from "jszip";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { createAvatar, generateAvatar } from "../../api/avatar/avatar";
import {
  DeleteIconAvatar,
  DownloadIcon,
  FemaleIcon,
  MaleIcon,
  OtherGenderIcon,
  SaveIcon,
  Spinner,
  UploadPhotoIcon,
} from "../../assets/icons/icons";
import { Gender } from "../../components/ai-avatar/avatar/Gender";
import ImageDialog from "../../components/ai-avatar/avatar/ImageDialog";
import AvatarFormDialog from "../../components/ai-avatar/dialog/AvatarFormDialog";
import FullscreenIcon from "../../assets/images/full-screen-round-icon.png";

export default function AiAvatar() {
  const [gender, setGender] = useState("Male");
  const [imagePreviews, setImagePreviews] = useState([]); // Store a single image preview
  const [prompt, setPrompt] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [apiImages, setApiImages] = useState([]);

  //Avatar Form Dialog
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // Error states
  const [imageError, setImageError] = useState("");
  const [promptError, setPromptError] = useState("");

  // Mutation for API call to generate avatar
  const avatarMutation = useMutation({
    mutationFn: generateAvatar, // function for the mutation
    onSuccess: (data) => {
      if (data) {
        const newImagePath = data?.data?.output_paths[0];
        if (newImagePath) {
          setApiImages((prev) => [...prev, newImagePath]);
        }
      } else {
        console.log("No data available in mutation response");
      }
    },
    onError: () => {
      toast.error("Failed to generate avatar.");
    },
  });

  const handleGenderChange = (selectedGender) => {
    setGender(selectedGender);
  };

  const handleFileChange = (e) => {
    setImageError("");
    const file = e.target.files[0];
    if (file) {
      const newPreview = URL.createObjectURL(file);
      setImagePreviews([newPreview]);
      setSelectedImage(file);
      setImageError("");
      e.target.value = "";
    } else {
      setImageError("Please upload a valid image.");
    }
  };

  // Handle the custom file upload button click
  const handleUploadClick = () => {
    document.getElementById("upload-photo").click(); // Trigger the hidden file input click
  };

  const handleSave = () => {
    let isValid = true;

    // Validate image
    if (imagePreviews.length === 0) {
      setImageError("Please upload an image.");
      isValid = false;
    } else {
      setImageError("");
    }

    // Validate prompt
    if (!prompt.trim()) {
      setPromptError("Please add a prompt.");
      isValid = false;
    } else {
      setPromptError("");
    }

    // If valid, proceed to API call to generate avatar
    if (isValid) {
      avatarMutation.mutate({ selectedImage, prompt, gender });
    }
  };

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
    setPromptError(""); // Clear error on valid input
  };

  const handleDelete = () => {
    setImagePreviews([]); // Clear the single image
    setApiImages([]);
    setGender("Male");
    setPrompt("");
    setImageError("");
    setPromptError("");
  };

  const handleImageClick = (img) => {
    setSelectedImage(img); // Save selected image
    setOpen(true); // Open dialog
  };

  const handleOpenDialog = () => {
    setIsOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setIsOpenDialog(false);
    setIsEdit(false);
  };

  const handleDownloadImages = async () => {
    const newId = uuidv4();

    if (apiImages.length === 0) {
      toast.warn("No images available to download.");
      return;
    }

    try {
      if (apiImages.length === 1) {
        // Single image download
        const imageUrl = `https://storage.cognise.art${apiImages[0]}`;
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        // Create a download link
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `avatar_image${newId}.png`; // Set the desired file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Multiple images - ZIP them
        const zip = new JSZip();
        const imageFolder = zip.folder("avatars"); // Create folder in zip

        const imagePromises = apiImages.map(async (preview, index) => {
          const imageUrl = `https://storage.cognise.art${preview}`;
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const filename = `avatar_${index + 1}.png`;
          imageFolder.file(filename, blob);
        });

        await Promise.all(imagePromises);
        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `avatars ${newId}.zip`);
      }
    } catch (error) {
      console.error("Error downloading images:", error);
      toast.error("Failed to download images.");
    }
  };

  const handleCreateAvatar = (data, closeDialog) => {
    try {
      createAvatarMutation.mutate(data, {
        onSuccess: () => {
          closeDialog();
        },
        onError: (error) => {
          console.log("Error adding new category:", error);
          throw new Error(error); // Throw the error properly
        },
      });
    } catch (error) {
      console.log("Error adding new category:", error);
      throw new error(error);
    }
  };
  const createAvatarMutation = useMutation({
    mutationFn: (data) => createAvatar(data),
    onSuccess: () => {
      toast.success("Avatar created successfully!");
    },
  });
  return (
    <div className="relative rounded-[20px] border border-[#0075FF1A]">
      <div className="scrollbar-hide h-[calc(100vh-198px)] overflow-hidden overflow-y-auto">
        <ImageDialog
          open={open}
          onClose={() => setOpen(false)}
          selectedImage={selectedImage}
        />

        {/* Main Content */}
        <div className="panel mx-2 flex h-fit flex-col gap-0 overflow-y-auto py-4 lg:mx-[60px] lg:flex-row lg:gap-[40px]">
          {/* Left Panel */}
          <div className="noscroll mt-0 w-full overflow-y-auto rounded-[20px] border border-[#0075FF1A] p-[20px] lg:mt-[34px] lg:max-w-[400px]">
            <div className="mb-[24px]">
              <p className="mb-[12px] font-outfit text-[18px] font-medium leading-[22.68px] text-[#222222]">
                Gender
              </p>
              <div className="flex flex-row flex-wrap gap-[30px] lg:flex-nowrap">
                <Gender
                  text="Male"
                  icon={<MaleIcon isSelected={gender === "Male"} />}
                  isSelected={gender === "Male"}
                  onClick={() => handleGenderChange("Male")}
                />
                <Gender
                  text="Female"
                  icon={<FemaleIcon isSelected={gender === "Female"} />}
                  isSelected={gender === "Female"}
                  onClick={() => handleGenderChange("Female")}
                />
                <Gender
                  text="Other"
                  icon={<OtherGenderIcon isSelected={gender === "Other"} />}
                  isSelected={gender === "Other"}
                  onClick={() => handleGenderChange("Other")}
                />
              </div>
            </div>

            <div className="mb-[24px]">
              <label
                htmlFor="upload-photo"
                className="mb-[12px] font-outfit text-[18px] font-medium leading-[22.68px] text-[#222222]"
              >
                Upload Photo
              </label>
              <div
                onClick={handleUploadClick}
                className="mt-[12px] flex w-[100px] cursor-pointer items-center justify-center rounded-[10px] bg-[#0075FF1A] p-[30px]"
              >
                <UploadPhotoIcon />
              </div>
              <input
                type="file"
                id="upload-photo"
                required
                onChange={handleFileChange}
                className="hidden"
                accept="image/jpeg,image/jpg,image/png"
                disabled={avatarMutation.isPending}
              />
              {imageError && (
                <p className="mt-2 text-sm text-red-500">{imageError}</p>
              )}
              <div className="mt-[16px] flex w-fit">
                {imagePreviews.length > 0 && (
                  <div className="h-[50px] w-[50px] overflow-hidden rounded-full">
                    <img
                      src={imagePreviews[0]}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="mb-[24px]">
              <p className="mb-[12px] font-outfit text-[18px] font-medium leading-[22.68px] text-[#222222]">
                Add Prompt
              </p>
              <textarea
                value={prompt}
                onChange={handlePromptChange}
                placeholder="Add prompt here..."
                disabled={avatarMutation.isPending}
                className="h-[170px] w-full rounded-[5px] border-transparent bg-[#0075FF1A] p-2 font-outfit text-[14px] font-[400] outline-none focus:border-[#0075FF0D]"
              />
              {promptError && (
                <p className="mt-2 text-sm text-red-500">{promptError}</p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                className={`${avatarMutation.isPending ? "opacity-50" : "opacity-100"} rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-700`}
                onClick={handleSave}
                disabled={avatarMutation.isPending}
              >
                {avatarMutation.isPending ? (
                  <div className="flex flex-row items-center justify-center gap-1">
                    <Spinner />
                    <p> Generating...</p>
                  </div>
                ) : (
                  "Generate"
                )}
              </button>
            </div>
          </div>

          {/* Right Panel */}
          <div className="noscroll mt-[34px] w-full overflow-y-auto rounded-[20px] border border-[#0075FF1A] p-[20px]">
            <div className="relative min-h-[250px] w-full rounded-[20px] border border-[#0075FF1A] bg-[#0075FF0D] lg:min-h-[calc(100%-106px)]">
              <div className="absolute right-[10px] top-[10px]"></div>

              <div className="noscroll flex flex-wrap gap-2 overflow-auto p-2">
                {apiImages.map((preview, index) => {
                  return (
                    <div
                      key={index}
                      className="group relative mx-4 h-[150px] w-[150px] overflow-hidden rounded-lg"
                    >
                      <img
                        src={`https://storage.cognise.art${preview}`}
                        alt={`Preview ${index}`}
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute right-[10px] top-[10px] cursor-pointer opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <img
                          src={FullscreenIcon}
                          alt="Fullscreen Icon"
                          className="h-8 w-8"
                          onClick={() =>
                            handleImageClick(
                              `https://storage.cognise.art${preview}`,
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-row justify-center gap-[30px] py-[28px]">
              <div
                className="rounded-[10px] bg-[#0075FF0D] p-[9px]"
                onClick={handleOpenDialog}
              >
                <SaveIcon />
              </div>
              <div
                className="rounded-[10px] bg-[#0075FF0D] p-[9px]"
                onClick={handleDownloadImages}
              >
                <DownloadIcon />
              </div>
              <div
                className="rounded-[10px] bg-[#0075FF0D] p-[9px]"
                onClick={() => handleDelete()}
              >
                <DeleteIconAvatar />
              </div>
            </div>
          </div>
        </div>
        <AvatarFormDialog
          isOpen={isOpenDialog}
          onClose={handleCloseDialog}
          handleSave={handleCreateAvatar}
          isPending={createAvatarMutation.isPending}
          prompt={prompt}
        />
      </div>
    </div>
  );
}
